import { Component, HostListener, ChangeDetectorRef, Renderer2, Inject } from "@angular/core";
import { Router, ActivatedRoute, NavigationEnd } from "@angular/router";
import { NotificationService } from "@apps/patient-core/src/app/services/notification.service";
import { OverlayService } from "@apps/patient-core/src/app/services/overlay.service";
import { PushUpdatesService } from "@apps/patient-core/src/app/services/push-updates.service";
import { FADE_IN_ANIMATION } from "@apps/patient-core/src/app/utils/animations";
import { SHARED } from "@apps/patient-core/src/app/shared";
import { NavigationService } from "@apps/patient-core/src/app/services/navigation.service";
import { CacheService } from "@apps/patient-core/src/app/services/cache.service";
import { Constants } from "@apps/concierge-app/constants";
import { ConciergeMainComponent } from "./components/main/main.component";
import { NotificationComponent } from "@apps/patient-core/src/app/components/notification/notification.component";
import { PwaService } from "./services/pwa.service";
import { BaseAppComponent } from "@apps/patient-core/src/app/components/abstracts/app/app.component";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { BaseSessionService } from "@apps/patient-core/src/app/services/abstracts/session.service";
import { tap } from "rxjs/operators";
import { filter } from "rxjs/operators";
import { SubSink } from "subsink";
import { DOCUMENT } from "@angular/common";
import { ConnectionStatusService } from "./services/connection-status.service";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  standalone: true,
  imports: [NotificationComponent, SHARED, ConciergeMainComponent],
  animations: [FADE_IN_ANIMATION],
})
export class ConciergeAppComponent extends BaseAppComponent {
  private _subscriptions = new SubSink();

  @HostListener("document:keydown.escape", ["$event"]) onKeydownHandler() {
    this.closeOverlay();
  }
  @HostListener("window:beforeunload", ["$event"]) beforeUnloadHandler() {
    this._pushUpdatesService.disconnect();
  }

  constructor(
    protected _router: Router,
    protected _authService: BaseAuthService,
    protected _activatedRoute: ActivatedRoute,
    protected _overlayService: OverlayService,
    protected _changeDetectorRef: ChangeDetectorRef,
    protected _renderer: Renderer2,
    protected _pushUpdatesService: PushUpdatesService,
    protected _notificationService: NotificationService,
    protected _navigationService: NavigationService,
    protected _cacheService: CacheService,
    protected _sessionService: BaseSessionService,
    @Inject(DOCUMENT) protected _document: Document,
    private _pwaService: PwaService,
    public connectionService: ConnectionStatusService
  ) {
    super(
      _router,
      _authService,
      _activatedRoute,
      _overlayService,
      _changeDetectorRef,
      _renderer,
      _pushUpdatesService,
      _notificationService,
      _navigationService,
      _cacheService,
      _sessionService,
      _document
    );
    this._subscriptions.sink = this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap((_event: NavigationEnd) => {
          const has_shortcut = this._activatedRoute.snapshot.queryParamMap.get("shortcut");
          if (has_shortcut) {
            this._cacheService.setSession(Constants.PWA_SHORTCUT_STORAGE_KEY, has_shortcut);
          }
        })
      )
      .subscribe();
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
  }

  override async ngOnInit() {
    super.ngOnInit();
    this.connectionService.pollConnection();
    await this._pwaService.unregisterServiceWorker();

    const isPatient = this._authService.isPatientInPractice();
    const pwaStorageShortcut = this._cacheService.getSession(Constants.PWA_SHORTCUT_STORAGE_KEY);

    // If we have a saved shortcut in session storage from clicking a PWA shortcut, navigate to that page
    if (pwaStorageShortcut && isPatient) {
      this._navigationService.navigate(`/my-dental/${pwaStorageShortcut}`);
      this._cacheService.deleteSession(Constants.PWA_SHORTCUT_STORAGE_KEY);
    }
  }
}
